/**
 * Loads the store locator index JSON (Shopify File URL from shop metafields),
 * injects list items + map templates before the map bundle initializes.
 */

function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function storeTargetName(storeId) {
    return `store_${storeId}`;
  }

  function buildAddressHtml(store) {
    const parts = [];

    if (store.address) {
      parts.push(escapeHtml(store.address).replace(/\n/g, '<br>'));
    }

    if (store.zip || store.city) {
      parts.push(
        `<div class="map-place-list__list-address-city">${escapeHtml(store.zip || '')}&nbsp;${escapeHtml(store.city || '')}</div>`
      );
    }

    if (store.country) {
      parts.push(
        `<div class="map-place-list__list-address-country">${escapeHtml(store.country)}</div>`
      );
    }

    return parts.join('');
  }

  function buildModalAddressHtml(store) {
    const parts = [];

    if (store.address) {
      parts.push(escapeHtml(store.address).replace(/\n/g, '<br>'));
    }

    if (store.zip || store.city) {
      parts.push(
        `<div class="map-place-list__modal-city">${escapeHtml(store.zip || '')}&nbsp;${escapeHtml(store.city || '')}</div>`
      );
    }

    if (store.country) {
      parts.push(`<div class="map-place-list__modal-country">${escapeHtml(store.country)}</div>`);
    }

    return parts.join('');
  }

  function buildContactRows(store, config, variant) {
    const display = config.display || {};
    const labels = config.labels || {};
    const prefix = variant === 'modal' ? 'map-place-list__modal' : 'map-place-list__list';
    const rows = [];

    const showPhone = variant === 'modal' ? display.modalPhone : display.phone;
    const showEmail = variant === 'modal' ? display.modalEmail : display.email;
    const showLink = variant === 'modal' ? display.modalLink : display.link;

    if (showPhone && store.phone) {
      rows.push(
        `<div class="${prefix}-store-contacts"><span>${escapeHtml(labels.phone)}</span> <a href="callto:${escapeHtml(store.phone)}">${escapeHtml(store.phone)}</a></div>`
      );
    }

    if (showEmail && store.email) {
      rows.push(
        `<div class="${prefix}-store-contacts"><span>${escapeHtml(labels.email)}</span> <a href="mailto:${escapeHtml(store.email)}">${escapeHtml(store.email)}</a></div>`
      );
    }

    if (showLink && store.link) {
      rows.push(
        `<div class="${prefix}-store-contacts"><span>${escapeHtml(labels.link)}</span> <a href="${escapeHtml(store.link)}">${escapeHtml(store.link)}</a></div>`
      );
    }

    return rows.join('');
  }

  function buildListStoreElement(store, config) {
    const target = storeTargetName(store.id);
    const storeName = config.storeName || {};
    const el = document.createElement('div');

    el.className = 'map-place-list__list-store';
    el.setAttribute('map-target', target);
    el.setAttribute('data-skus-json', JSON.stringify(store.skus || []));
    el.setAttribute('data-search-address', store.searchAddress || '');
    el.setAttribute('data-latitude', store.lat || '');
    el.setAttribute('data-longitude', store.lng || '');

    const seeOnMapClass = `button button--${config.buttons?.seeOnMapStyle || 'transparent'} button--small button--fullwidth map-place-list__list-store-see-map-btn`;

    el.innerHTML = `
      <div class="map-place-list__list-store-infos">
        <${storeName.tag || 'h3'}
          class="map-place-list__list-store-name ${escapeHtml(storeName.headingClass || 'heading h6')}"
          ${storeName.inlineStyle ? `style="${escapeHtml(storeName.inlineStyle)}"` : ''}
        >${escapeHtml(store.name)}</${storeName.tag || 'h3'}>
        <div class="map-place-list__list-address">${buildAddressHtml(store)}</div>
        <div class="map-place-list__list-store-contact-list">${buildContactRows(store, config, 'list')}</div>
        <button type="button" class="${seeOnMapClass}">${escapeHtml(config.labels?.seeOnMap || '')}</button>
      </div>
    `;

    return el;
  }

  function buildMapTemplate(store, config) {
    const target = storeTargetName(store.id);
    const storeName = config.storeName || {};
    const template = document.createElement('template');

    template.setAttribute('name', target);
    template.setAttribute(
      'address',
      [store.address, store.zip, store.city, store.country].filter(Boolean).join(' ')
    );
    template.setAttribute('latitude', store.lat || '');
    template.setAttribute('longitude', store.lng || '');
    template.setAttribute('data-skus-json', JSON.stringify(store.skus || []));
    template.setAttribute('data-search-address', store.searchAddress || '');
    template.setAttribute('data-latitude', store.lat || '');
    template.setAttribute('data-longitude', store.lng || '');

    const navigateButton = config.display?.modalNavigation
      ? `<div class="map-place-list__modal-store-navigation">
          <a class="button button--${escapeHtml(config.buttons?.navigateStyle || 'transparent')} button--small" target="_blank" navigate-button>${escapeHtml(config.labels?.navigate || '')}</a>
        </div>`
      : '';

    template.innerHTML = `
      <div class="map-place-list__card map-place-list__modal-store">
        <div class="map-place-list__modal-store-infos">
          <${storeName.tag || 'h3'}
            class="map-place-list__modal-store-name ${escapeHtml(storeName.headingClass || 'heading h6')}"
            ${storeName.inlineStyle ? `style="${escapeHtml(storeName.inlineStyle)}"` : ''}
          >${escapeHtml(store.name)}</${storeName.tag || 'h3'}>
          <div class="map-place-list__modal-address">${buildModalAddressHtml(store)}</div>
          <div class="map-place-list__modal-store-opening-hours"></div>
          <div class="map-place-list__modal-store-contact-list">${buildContactRows(store, config, 'modal')}</div>
          ${navigateButton}
        </div>
      </div>
    `;

    return template;
  }


  function buildStoreRegistry(mapId, stores) {
    const registry = {
      byName: new Map(),
      allNames: [],
      skuToStores: new Map(),
    };

    stores.forEach((store) => {
      const name = storeTargetName(store.id);
      const skus = (store.skus || [])
        .map((sku) => String(sku).trim().toLowerCase())
        .filter(Boolean);

      registry.byName.set(name, {
        name,
        lat: parseFloat(store.lat),
        lng: parseFloat(store.lng),
        skus,
        skuSet: new Set(skus),
        searchAddress: store.searchAddress || '',
        store,
      });
      registry.allNames.push(name);

      skus.forEach((sku) => {
        if (!registry.skuToStores.has(sku)) registry.skuToStores.set(sku, new Set());
        registry.skuToStores.get(sku).add(name);
      });
    });

    window.__mapPlaceListRegistry = window.__mapPlaceListRegistry || {};
    window.__mapPlaceListRegistry[mapId] = registry;
    return registry;
  }

  function parseSkusFromTemplate(template) {
    const json = template.getAttribute('data-skus-json');
    if (json) {
      try {
        const parsed = JSON.parse(json);
        if (Array.isArray(parsed)) {
          return parsed.map((sku) => String(sku).trim().toLowerCase()).filter(Boolean);
        }
      } catch {
        // Fall back to CSV attribute when JSON is invalid.
      }
    }

    return (template.getAttribute('data-skus') || '')
      .split(/[,;•·|]/)
      .map((sku) => sku.trim().toLowerCase())
      .filter(Boolean);
  }

  function installMapViewerPerformancePatches() {
    if (window.__mapPlaceListPerfPatchesInstalled) return;
    window.__mapPlaceListPerfPatchesInstalled = true;

    const originalDefine = customElements.define.bind(customElements);
    customElements.define = function mapPlaceListDefine(name, klass, options) {
      if (name === 'map-viewer') patchMapViewerClass(klass);
      return originalDefine(name, klass, options);
    };
  }

  function patchMapViewerClass(MapViewer) {
    if (MapViewer.prototype.__mapPlaceListPerfPatched) return;
    MapViewer.prototype.__mapPlaceListPerfPatched = true;

    const originalGrabTemplates = MapViewer.prototype.grabTemplates;
    MapViewer.prototype.grabTemplates = function grabTemplatesLazy() {
      const mapId = this.getAttribute('map-id');
      const registry = window.__mapPlaceListRegistry?.[mapId];

      if (registry) {
        registry.allNames.forEach((name) => {
          const entry = registry.byName.get(name);
          const store = entry.store;

          this.places.set(name, {
            templateEl: null,
            node: null,
            navigateButton: null,
            address: [store.address, store.zip, store.city, store.country].filter(Boolean).join(' '),
            longitude: String(entry.lng),
            latitude: String(entry.lat),
            name,
            skus: entry.skus,
          });
        });
        return;
      }

      [...this.querySelectorAll('template')].forEach((template) => {
        const name = template.getAttribute('name');
        if (!name) return;

        this.places.set(name, {
          templateEl: template,
          node: null,
          navigateButton: null,
          address: template.getAttribute('address') || '',
          longitude: template.getAttribute('longitude') || null,
          latitude: template.getAttribute('latitude') || null,
          name,
          skus: parseSkusFromTemplate(template),
        });
      });

      if (typeof originalGrabTemplates === 'function' && this.places.size === 0) {
        originalGrabTemplates.call(this);
      }
    };

    const originalDisplay = MapViewer.prototype.display;
    MapViewer.prototype.display = function displayLazy(place) {
      if (place && !place.node) {
        const source = place.templateEl || this.querySelector(`template[name="${place.name}"]`);
        if (source?.content?.firstElementChild) {
          place.node = source.content.firstElementChild.cloneNode(true);
          place.navigateButton = place.node.querySelector('[navigate-button]');
        }
      }

      return originalDisplay.call(this, place);
    };

    const originalAddPlaces = MapViewer.prototype.addPlaces;
    MapViewer.prototype.addPlaces = async function addPlacesLazy(place) {
      let position = null;

      if (place.latitude !== null && place.longitude !== null) {
        const lat = parseFloat(place.latitude);
        const lng = parseFloat(place.longitude);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          position = new google.maps.LatLng(lat, lng);
        }
      }

      if (!position) {
        return originalAddPlaces.call(this, place);
      }

      const existing = this.places.get(place.name);
      if (existing?.marker) return;

      this.places.set(place.name, {
        ...(existing || {}),
        ...place,
        location: position,
        marker: null,
      });
    };

    const originalInit = MapViewer.prototype.init;
    MapViewer.prototype.init = async function initWithDefaultCenter() {
      await originalInit.call(this);

      if (!this.map) return;

      const lat = parseFloat(this.getAttribute('default-center-lat'));
      const lng = parseFloat(this.getAttribute('default-center-lng'));
      const zoom = parseInt(this.getAttribute('default-zoom-level'), 10);
      const center = {
        lat: Number.isFinite(lat) ? lat : 40.4168,
        lng: Number.isFinite(lng) ? lng : -3.7038,
      };
      const defaultZoom = Number.isFinite(zoom) && zoom > 0 ? zoom : 10;

      this.map.setCenter(center);
      this.map.setZoom(defaultZoom);
    };
  }

  async function fetchStoreIndex(config) {
    const url = new URL(config.indexUrl, window.location.origin);
    if (config.indexVersion) {
      url.searchParams.set('v', config.indexVersion);
    }

    const response = await fetch(url.toString(), { credentials: 'omit' });
    if (!response.ok) {
      throw new Error(`Store index fetch failed (${response.status})`);
    }

    return response.json();
  }

  function injectStores(config, stores) {
    const mapViewer = document.getElementById(config.mapViewerId);
    const controllers = document.querySelectorAll(`map-controller[map-id="${config.mapId}"]`);

    if (!mapViewer || !controllers.length) {
      throw new Error('Store locator containers not found');
    }

    const listFragment = document.createDocumentFragment();
    const templateFragment = document.createDocumentFragment();

    stores.forEach((store) => {
      listFragment.appendChild(buildListStoreElement(store, config));
      templateFragment.appendChild(buildMapTemplate(store, config));
    });

    controllers.forEach((controller) => {
      controller.replaceChildren(listFragment.cloneNode(true));
    });

    mapViewer.appendChild(templateFragment);
  }

  async function loadSection(config) {
    const payload = await fetchStoreIndex(config);
    const stores = Array.isArray(payload?.stores) ? payload.stores : [];

    injectStores(config, stores);
    buildStoreRegistry(config.mapId, stores);

    window.__mapPlaceListStoreIndex = window.__mapPlaceListStoreIndex || {};
    window.__mapPlaceListStoreIndex[config.mapId] = payload;

    document.dispatchEvent(
      new CustomEvent('map-place-list:stores-loaded', {
        bubbles: true,
        detail: {
          mapId: config.mapId,
          count: stores.length,
          version: config.indexVersion || null,
        },
      })
    );

  return stores.length;
}

async function initAll() {
  const configElements = [...document.querySelectorAll('script[id^="map-place-list-config-"]')];

  if (!configElements.length) {
    window.__mapPlaceListStoresReady = true;
    return;
  }

  const configs = [];

  for (const element of configElements) {
    const config = JSON.parse(element.textContent || '{}');
    if (!config.indexUrl || !config.mapId) {
      throw new Error('Store locator config is missing indexUrl or mapId');
    }

    await loadSection(config);
    configs.push(config);
  }

  window.__mapPlaceListStoresReady = true;

  // Load the map bundle only after store templates are in the DOM. If the bundle
  // initializes first it finds zero templates and never creates markers.
  installMapViewerPerformancePatches();
  const bundleModuleUrls = [...new Set(configs.map((config) => config.bundleModuleUrl).filter(Boolean))];
  for (const bundleModuleUrl of bundleModuleUrls) {
    await import(/* @vite-ignore */ bundleModuleUrl);
  }
}

try {
  await initAll();
} catch (error) {
  console.error('[map-place-list-index]', error);
  window.__mapPlaceListStoresError = error;
  document.dispatchEvent(
    new CustomEvent('map-place-list:stores-error', {
      bubbles: true,
      detail: { message: error.message },
    })
  );
  throw error;
}
